/**
 * Flashcard: four options, one correct.
 *
 * A correct answer prompts a second tap — "Knew it" or "Guessed". This is the
 * most important interaction in the app: self-reported confidence at the
 * moment of recall maps directly onto the FSRS grade scale, and without it
 * everything correct collapses to grade 3 (docs/01, docs/02).
 *
 * The prompt is a sheet, not an inline row, and it is modal on purpose — an
 * unanswered confidence prompt is a missing grade.
 */

import type { AttemptRecord, FlashcardPayload } from "../types.ts";
import { el, gloss, primaryButton } from "../ui.ts";
import { uuid, type BlockContext, type RenderedBlock } from "./types.ts";

export function renderFlashcard(context: BlockContext): RenderedBlock {
	const payload = context.block.payload as unknown as FlashcardPayload;
	const shownAt = Date.now();
	let answered = false;

	const body: Node[] = [];

	const promptIsPt = payload.prompt_lang === "pt";
	const prompt = el("div", {
		class: "display",
		lang: promptIsPt ? "pt-BR" : "en",
		text: payload.prompt,
	});
	body.push(el("div", { class: "prompt-word" }, prompt));

	const list = el("div", { role: "group", "aria-label": "Answer options" });
	body.push(list);

	const noteHolder = el("div");
	body.push(noteHolder);

	const buttons: HTMLButtonElement[] = [];
	for (const option of payload.options) {
		const button = el("button", {
			class: "opt",
			type: "button",
			lang: promptIsPt ? "en" : "pt-BR",
		});
		button.append(el("span", { text: option }));
		button.addEventListener("click", () => {
			if (answered) return;
			answered = true;
			reveal(option);
		});
		buttons.push(button);
		list.append(button);
	}

	const reveal = (chosen: string): void => {
		const correct = chosen === payload.answer;
		const elapsed = Date.now() - shownAt;

		buttons.forEach((button, index) => {
			const optionText = payload.options[index] ?? "";
			button.disabled = true;
			// Never encode state in colour alone: correct carries a check, wrong
			// reveals the correction inline (docs/05).
			if (optionText === payload.answer) {
				button.classList.add("opt--correct");
				button.append(el("span", { class: "opt__tick", text: "✓", "aria-label": "correct answer" }));
			} else if (optionText === chosen) {
				button.classList.add("opt--wrong");
				button.append(el("span", { class: "opt__tick", text: "✕", "aria-label": "your answer" }));
			}
		});

		if (payload.note_en.trim() !== "") {
			const note = gloss(payload.note_en, context.englishRatio);
			if (note !== null) noteHolder.append(note);
		}

		if (context.isTest) {
			// No feedback colour reaches the learner mid-test, and no confidence
			// prompt either — the answer is simply recorded.
			buttons.forEach((button) => {
				button.classList.remove("opt--correct", "opt--wrong");
				if (button.textContent?.startsWith(chosen) === true) button.classList.add("opt--selected");
				button.querySelector(".opt__tick")?.remove();
			});
			noteHolder.replaceChildren();
			complete(correct, null, elapsed);
			return;
		}

		showConfidenceSheet(correct, elapsed);
	};

	const complete = (correct: boolean, confidence: "knew" | "guessed" | null, elapsedMs: number): void => {
		const attempt: AttemptRecord = {
			id: uuid(),
			session_id: context.session.id,
			block_id: context.block.id,
			item_id: payload.item_id === 0 ? null : payload.item_id,
			modality: "recognition",
			correct,
			confidence,
			response_text: null,
			elapsed_ms: elapsedMs,
			created_at: Date.now(),
		};
		context.finish({ attempts: [attempt] });
	};

	const showConfidenceSheet = (correct: boolean, elapsedMs: number): void => {
		if (!correct) {
			// A wrong answer is already a grade 1; the correction is on screen, so
			// the learner just moves on when ready.
			context.setFooter(primaryButton("Continue", () => complete(false, null, elapsedMs)));
			return;
		}

		const scrim = el("div", { class: "scrim" });
		const sheet = el(
			"div",
			{ class: "sheet", role: "dialog", "aria-modal": "true", "aria-label": "Did you know it, or guess?" },
			el("p", { class: "en en--ink center", style: "font-size:15px", text: "Did you know it, or guess?" }),
			el(
				"div",
				{ class: "sheet__row" },
				el("button", {
					class: "btn-knew",
					type: "button",
					text: "Knew it",
					onclick: () => {
						dismiss();
						complete(true, "knew", elapsedMs);
					},
				}),
				el("button", {
					class: "btn-guessed",
					type: "button",
					text: "Guessed",
					onclick: () => {
						dismiss();
						complete(true, "guessed", elapsedMs);
					},
				}),
			),
			el("p", {
				class: "en center",
				style: "margin-top:11px;font-size:12px",
				text: "Guessing schedules it sooner. It isn't a wrong answer.",
			}),
		);

		const dismiss = (): void => {
			scrim.remove();
			sheet.remove();
		};

		document.body.append(scrim, sheet);
		sheet.querySelector<HTMLButtonElement>(".btn-knew")?.focus();
	};

	return { body, footer: null };
}
